import { isLocalMcpUrl } from '@littlebird/axon-mcp/contract';
import {
  cleanupPendingMCPServerForUrl,
  extractMCPErrorMessage,
  isMCPServerNameTaken,
  normalizeMCPUrl,
  pollMCPServerStatus,
  type OAuthReturnTo,
} from '@littlebird/mcp';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

import { Icons } from '@/assets/icons/icons';
import { backendApis } from '@/backendApis';
import {
  type BaseModalFCProps,
  type ModalFC,
  ModalsManager,
} from '@/components/modals/modalsManager';
import { Button } from '@/components/ui/Button';
import { Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/Input';
import { XStack, YStack } from '@/components/ui/layout/layout';
import { FEATURES, useFeature } from '@/features/feature-flags';
import { integrationKeys } from '@/features/integrations/integrations.query';
import { LocalMcpAddServerModal } from '@/features/local-mcp';
import { runOAuthPoll, stopOAuthPoll } from '@/features/mcp/mcp.oauth-poll';
import {
  mcpKeys,
  useMCPCatalogQuery,
  useMCPServersQuery,
} from '@/features/mcp/mcp.query';
import { currentPageReturnTo } from '@/integrations/oauth-return';
import { inDesktop } from '@/utils/capabilities';
import { handleExternalUrlClick } from '@/utils/platform';

type Step =
  | { type: 'url' }
  | { type: 'auto_connecting' }
  | {
      type: 'api_key';
      serverName: string | null;
      // True when the server advertised OAuth but we couldn't auto-register it,
      // so we landed here as a fallback rather than a native api_key server.
      oauthFallback?: boolean;
      // True when the user chose to bypass a working OAuth flow (stuck consent
      // page, org policy blocks, etc.) and connect with a key instead.
      oauthEscape?: boolean;
    }
  | { type: 'oauth'; serverName: string | null }
  | { type: 'oauth_polling'; serverId: string; serverName: string }
  | { type: 'oauth_success' }
  | { type: 'oauth_error'; reason: 'failed' | 'timeout'; detail?: string }
  | { type: 'oauth_cancelled' };

export interface MCPAddServerModalProps extends BaseModalFCProps {
  initialUrl?: string;
  initialName?: string;
  reconnectServerId?: string;
  reconnectServerName?: string;
  // flow_id from reconnectServer(); scopes an explicit Cancel to this exact flow
  reconnectFlowId?: string;
  /**
   * false at most once per attempt (cancel, error, dismiss); true whenever the
   * flow connects, so true may follow false
   */
  onOAuthSettled?: (success: boolean) => void;
  returnTo?: OAuthReturnTo;
}

export const MCPAddServerModal: ModalFC<MCPAddServerModalProps> = ({
  open,
  onOpenChange,
  initialUrl,
  initialName,
  reconnectServerId,
  reconnectServerName,
  reconnectFlowId,
  onOAuthSettled,
  returnTo,
}) => {
  const queryClient = useQueryClient();
  const hasFeature = useFeature();
  const customLocalMcpEnabled =
    inDesktop() && hasFeature(FEATURES.CUSTOM_LOCAL_MCP);
  const { data: existingServers } = useMCPServersQuery();
  const { data: catalogEntries } = useMCPCatalogQuery();
  // A reconnect enters on the browser wait for an existing server; everything
  // else starts from a URL
  const reconnect =
    reconnectServerId && reconnectServerName
      ? {
          serverId: reconnectServerId,
          serverName: reconnectServerName,
          flowId: reconnectFlowId ?? null,
        }
      : null;
  const [step, setStep] = useState<Step>(() => {
    if (reconnect) {
      return {
        type: 'oauth_polling',
        serverId: reconnect.serverId,
        serverName: reconnect.serverName,
      };
    }
    if (initialUrl && initialName) return { type: 'auto_connecting' };
    return { type: 'url' };
  });
  const [url, setUrl] = useState(initialUrl ?? '');
  const [name, setName] = useState(reconnect?.serverName ?? initialName ?? '');
  const [apiToken, setApiToken] = useState('');
  const [basicAuthUsername, setBasicAuthUsername] = useState('');
  const [showUsernameField, setShowUsernameField] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const MAX_ATTEMPTS = 60;
  const POLL_INTERVAL_MS = 5000;

  // Bumped on dismiss so a stale initiate result cannot open the browser
  const generationRef = useRef(0);
  const oauthSettlementReportedRef = useRef(false);
  // The flow Cancel abandons: set for the browser wait, cleared at poll end
  const pendingServerIdRef = useRef<string | null>(reconnect?.serverId ?? null);
  const pendingFlowIdRef = useRef<string | null>(reconnect?.flowId ?? null);

  const reportOAuthSettlement = useCallback(
    (success: boolean) => {
      if (!success && oauthSettlementReportedRef.current) return;
      oauthSettlementReportedRef.current = true;
      onOAuthSettled?.(success);
    },
    [onOAuthSettled]
  );

  // Supersede the attempt: a running poll ends without reporting
  const cleanup = () => {
    generationRef.current++;
    if (step.type === 'oauth_polling') stopOAuthPoll(step.serverId);
  };

  // Unmount must not stop the poll: the user may still be authorizing
  useEffect(() => {
    return () => {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      generationRef.current++;
    };
  }, []);

  const handleAutoConnect = async (targetUrl: string, serverName: string) => {
    oauthSettlementReportedRef.current = false;
    const gen = generationRef.current;
    const isStale = () => gen !== generationRef.current;

    setStep({ type: 'auto_connecting' });
    setUrl(targetUrl);
    setName(serverName);
    try {
      const result = await backendApis.mcp.discoverServer(targetUrl);
      if (isStale()) return;

      if (result.auth_type !== 'oauth') {
        if (result.server_name) setName(result.server_name);
        setStep({
          type: 'api_key',
          serverName: result.server_name ?? null,
          oauthFallback: result.oauth_fallback ?? false,
        });
        return;
      }

      // Fetch fresh server list to avoid relying on possibly-stale React Query cache.
      const freshServers = await backendApis.mcp.listServers();
      if (isStale()) return;
      await cleanupPendingMCPServerForUrl(
        backendApis.mcp,
        targetUrl,
        freshServers
      );
      if (isStale()) return;

      const oauthResult = await backendApis.mcp.initiateOAuth(
        targetUrl,
        serverName,
        returnTo ?? currentPageReturnTo()
      );
      if (isStale()) {
        // Closed mid-initiation — cancel (not delete) so the entered URL survives.
        backendApis.mcp
          .cancelPendingOAuth(oauthResult.server_id, oauthResult.flow_id)
          .catch(() => {});
        queryClient.invalidateQueries({ queryKey: mcpKeys.servers() });
        return;
      }
      pendingServerIdRef.current = oauthResult.server_id;
      pendingFlowIdRef.current = oauthResult.flow_id;
      handleExternalUrlClick(oauthResult.redirect_url, {
        forceInBrowser: true,
      });
      setStep({
        type: 'oauth_polling',
        serverId: oauthResult.server_id,
        serverName,
      });
      startOAuthPolling(oauthResult.server_id);
    } catch (error) {
      if (isStale()) return;
      console.error('Auto-connect failed:', error);

      setStep({
        type: 'oauth_error',
        reason: 'failed',
        detail: extractMCPErrorMessage(error, 'Failed to connect'),
      });
    }
  };

  // Starts the work the props ask for: the reconnect's status poll, or
  // discovery of the given URL
  useEffect(() => {
    if (reconnect) {
      startOAuthPolling(reconnect.serverId);
    } else if (initialUrl && initialName) {
      handleAutoConnect(initialUrl, initialName);
    } else if (initialUrl) {
      handleDiscover(initialUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // URL to offer the API-key fallback against. The reconnect flow enters
  // without url state, so recover it from the server row being reconnected.
  const fallbackUrl = (
    url.trim() ||
    initialUrl ||
    (reconnectServerId
      ? (existingServers?.find((s) => s.id === reconnectServerId)?.url ?? '')
      : '')
  ).trim();

  // Marks the flow cancelled, not deleted, so the row stays retryable
  const cancelPendingFlow = () => {
    const serverId = pendingServerIdRef.current;
    if (!serverId) return;
    const flowId = pendingFlowIdRef.current ?? '';
    pendingServerIdRef.current = null;
    pendingFlowIdRef.current = null;
    backendApis.mcp
      .cancelPendingOAuth(serverId, flowId)
      .catch(() => {})
      .finally(() =>
        queryClient.invalidateQueries({ queryKey: mcpKeys.servers() })
      );
  };

  // User-directed bail-out of OAuth onto the api_key step for the same server
  const switchToApiKeyFallback = () => {
    cleanup();
    cancelPendingFlow();
    setUrl(fallbackUrl);
    setStep({
      type: 'api_key',
      serverName: name.trim() || null,
      oauthEscape: true,
    });
  };

  const resetState = () => {
    setStep({ type: 'url' });
    setUrl('');
    setName('');
    setApiToken('');
    setBasicAuthUsername('');
    setShowUsernameField(false);
    setIsLoading(false);
  };

  // In Hummingbird the overlay holds the mouse, so the first click toward the
  // browser hits the backdrop: dismissing keeps the flow and its poll
  const dismiss = () => {
    generationRef.current++;
    reportOAuthSettlement(false);
    onOpenChange(false);
  };

  // The poll keeps running so the list converges if a callback beat the cancel
  const cancelAndClose = () => {
    cancelPendingFlow();
    dismiss();
  };

  const openLocalMcpModal = (initialUrl?: string) => {
    dismiss();
    ModalsManager.show(LocalMcpAddServerModal, { initialUrl });
  };

  const isNameTaken = () => isMCPServerNameTaken(name, existingServers);

  const cleanupStaleServer = async () => {
    // Only clean up orphaned pending records from incomplete OAuth flows
    // Don't delete expired/unauthorized servers — they have metadata worth keeping
    await cleanupPendingMCPServerForUrl(
      backendApis.mcp,
      url.trim(),
      existingServers ?? []
    );
  };

  const handleDiscover = async (overrideUrl?: string) => {
    const targetUrl = (overrideUrl ?? url).trim();
    if (!targetUrl) return;
    if (customLocalMcpEnabled && isLocalMcpUrl(targetUrl)) {
      openLocalMcpModal(targetUrl);
      return;
    }
    if (!overrideUrl) {
      const existing = existingServers?.find(
        (s) => normalizeMCPUrl(s.url) === normalizeMCPUrl(targetUrl)
      );
      if (existing && existing.status === 'connected') {
        toast.error('This server is already connected');
        return;
      }
    }
    setIsLoading(true);
    try {
      const result = await backendApis.mcp.discoverServer(targetUrl);

      // If the URL matches a catalog entry, prefer the catalog's curated name
      // (and icon, via MCPIcon's URL match) over the server-advertised name so a
      // custom add presents identically to a catalog add.
      const catalogEntry = catalogEntries?.find(
        (e) => normalizeMCPUrl(e.url) === normalizeMCPUrl(targetUrl)
      );
      const resolvedName = catalogEntry?.name ?? result.server_name ?? null;
      if (resolvedName) setName(resolvedName);

      if (result.auth_type === 'oauth') {
        setStep({
          type: 'oauth',
          serverName: resolvedName,
        });
      } else {
        setStep({
          type: 'api_key',
          serverName: resolvedName,
          oauthFallback: result.oauth_fallback ?? false,
        });
      }
    } catch (error) {
      console.error('Discovery failed:', error);
      toast.error(
        extractMCPErrorMessage(
          error,
          'Failed to discover server. Check the URL and try again.'
        )
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateApiKey = async () => {
    if (!name.trim()) return;
    if (isNameTaken()) {
      toast.error(`A server named "${name.trim()}" already exists`);
      return;
    }
    oauthSettlementReportedRef.current = false;
    setIsLoading(true);
    try {
      await cleanupStaleServer();
      await backendApis.mcp.createApiKeyServer(
        name.trim(),
        url.trim(),
        apiToken.trim(),
        { basicAuthUsername: basicAuthUsername.trim() }
      );
      queryClient.invalidateQueries({ queryKey: mcpKeys.servers() });
      toast.success(`${name} connected successfully!`);
      reportOAuthSettlement(true);
      dismiss();
    } catch (error) {
      console.error('Create API key server failed:', error);
      toast.error(extractMCPErrorMessage(error, 'Failed to connect server'));
    } finally {
      setIsLoading(false);
    }
  };

  const refreshServerList = () => {
    backendApis.mcp
      .listServers()
      .then((data) => queryClient.setQueryData(mcpKeys.servers(), data))
      .catch(() =>
        queryClient.invalidateQueries({ queryKey: mcpKeys.servers() })
      );
  };

  const startOAuthPolling = (serverId: string) => {
    runOAuthPoll(serverId, (signal) =>
      pollMCPServerStatus(backendApis.mcp, serverId, {
        maxAttempts: MAX_ATTEMPTS,
        intervalMs: POLL_INTERVAL_MS,
        signal,
      })
    )
      .then((result) => {
        // Superseded by a retry, a reopened dialog or sign-out
        if (result.type === 'aborted') return;
        pendingServerIdRef.current = null;
        pendingFlowIdRef.current = null;

        if (result.type === 'connected') {
          setStep({ type: 'oauth_success' });
          refreshServerList();
          // A connect can add a first prompt to try on Home and Integrations
          void queryClient.invalidateQueries({
            queryKey: integrationKeys.suggestions(),
          });
          reportOAuthSettlement(true);
          return;
        }

        if (result.type === 'cancelled' || result.type === 'expired') {
          setStep(
            result.type === 'cancelled'
              ? { type: 'oauth_cancelled' }
              : { type: 'oauth_error', reason: 'failed' }
          );
          refreshServerList();
          reportOAuthSettlement(false);
          return;
        }

        if (result.type === 'failed') {
          setStep({
            type: 'oauth_error',
            reason: 'failed',
            detail: extractMCPErrorMessage(result.error, 'Failed to connect'),
          });
          refreshServerList();
          reportOAuthSettlement(false);
          return;
        }

        if (result.type === 'timeout') {
          setStep({ type: 'oauth_error', reason: 'timeout' });
          reportOAuthSettlement(false);
        }
      })
      // pollMCPServerStatus never rejects by contract; this is a backstop so
      // an unexpected throw can't strand the modal on the polling spinner
      .catch((error) => {
        pendingServerIdRef.current = null;
        pendingFlowIdRef.current = null;
        setStep({
          type: 'oauth_error',
          reason: 'failed',
          detail: extractMCPErrorMessage(error, 'Failed to connect'),
        });
        reportOAuthSettlement(false);
      });
  };

  const handleOAuthConnect = async () => {
    if (!name.trim()) return;
    if (isNameTaken()) {
      toast.error(`A server named "${name.trim()}" already exists`);
      return;
    }
    oauthSettlementReportedRef.current = false;
    setIsLoading(true);
    try {
      await cleanupStaleServer();
      const result = await backendApis.mcp.initiateOAuth(
        url.trim(),
        name.trim(),
        returnTo ?? currentPageReturnTo()
      );
      pendingServerIdRef.current = result.server_id;
      pendingFlowIdRef.current = result.flow_id;
      handleExternalUrlClick(result.redirect_url, { forceInBrowser: true });
      setStep({
        type: 'oauth_polling',
        serverId: result.server_id,
        serverName: name,
      });
      startOAuthPolling(result.server_id);
    } catch (error) {
      console.error('OAuth initiation failed:', error);
      setStep({
        type: 'oauth_error',
        reason: 'failed',
        detail: extractMCPErrorMessage(
          error,
          'Failed to start OAuth connection'
        ),
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) dismiss();
      }}
    >
      <Dialog.Content className="max-w-lg gap-5 !z-[100] no-drag">
        <Dialog.Header>
          <Dialog.Title className="text-lg">
            Add {name.trim() || 'MCP Server'}
          </Dialog.Title>
        </Dialog.Header>

        {step.type === 'auto_connecting' && (
          <YStack center className="gap-4 py-4">
            <Icons.loader className="size-6 animate-spin text-foreground-muted" />
            <p className="text-sm text-foreground-muted">Connecting...</p>
          </YStack>
        )}

        {step.type === 'url' && (
          <YStack className="gap-5">
            <YStack className="gap-3">
              <label
                htmlFor="mcp-url"
                className="text-sm font-medium text-foreground"
              >
                Server URL
              </label>
              <Input
                id="mcp-url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleDiscover();
                }}
                placeholder="https://mcp.example.com/mcp"
                className="text-sm"
                autoFocus
              />
              <XStack className="gap-2 items-start rounded-lg border border-dashed border-border bg-surface px-3 py-2.5">
                <div className="flex h-5 items-center shrink-0">
                  <Icons.warnTriangle className="size-4 text-foreground-muted" />
                </div>
                <span className="text-xs leading-5 text-foreground-muted">
                  Custom MCP servers can access your data and take actions on
                  your behalf. Only add servers from sources you trust.
                </span>
              </XStack>
            </YStack>
            <Button
              onClick={() => handleDiscover()}
              disabled={!url.trim() || isLoading}
              className="w-full"
            >
              {isLoading ? (
                <Icons.loader className="size-4 animate-spin" />
              ) : (
                'Continue'
              )}
            </Button>
            {customLocalMcpEnabled && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => openLocalMcpModal()}
                className="self-center"
              >
                <Icons.terminal className="size-3.5" />
                Add a local server instead
              </Button>
            )}
          </YStack>
        )}

        {step.type === 'api_key' && (
          <YStack className="gap-4">
            {step.oauthFallback && (
              <p className="text-sm text-foreground-muted">
                This server advertises OAuth, but it doesn't support automatic
                setup. If it accepts a personal access token or other long-lived
                token, paste one below. Otherwise contact the server's provider.
              </p>
            )}
            {step.oauthEscape && (
              <p className="text-sm text-foreground-muted">
                If you have an API key or personal access token for this
                service, you can connect with it instead of OAuth. Some services
                require your account email alongside the token.
              </p>
            )}
            <YStack className="gap-2">
              <label
                htmlFor="mcp-name"
                className="text-sm font-medium text-foreground"
              >
                Name
              </label>
              <Input
                id="mcp-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleCreateApiKey();
                }}
                placeholder="Server name"
                className="text-sm"
                autoFocus
              />
            </YStack>
            <YStack className="gap-2">
              <label
                htmlFor="mcp-token"
                className="text-sm font-medium text-foreground"
              >
                API Token
                {!step.oauthFallback && (
                  <span className="font-normal text-foreground-muted">
                    {' '}
                    (optional)
                  </span>
                )}
              </label>
              <Input
                id="mcp-token"
                type="password"
                value={apiToken}
                onChange={(e) => setApiToken(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleCreateApiKey();
                }}
                placeholder={
                  step.oauthFallback || step.oauthEscape
                    ? 'Paste a token the server will accept'
                    : 'Leave blank if no auth required'
                }
                className="text-sm"
              />
            </YStack>
            {showUsernameField ? (
              <YStack className="gap-2">
                <label
                  htmlFor="mcp-basic-username"
                  className="text-sm font-medium text-foreground"
                >
                  Email or username
                  <span className="font-normal text-foreground-muted">
                    {' '}
                    (for servers that use Basic authentication)
                  </span>
                </label>
                <Input
                  id="mcp-basic-username"
                  value={basicAuthUsername}
                  onChange={(e) => setBasicAuthUsername(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleCreateApiKey();
                  }}
                  placeholder="you@example.com"
                  className="text-sm"
                />
              </YStack>
            ) : (
              <button
                type="button"
                className="self-start text-xs text-foreground-muted underline hover:text-foreground"
                onClick={() => setShowUsernameField(true)}
              >
                My token needs an email or username (Basic auth)
              </button>
            )}
            <Button
              onClick={handleCreateApiKey}
              disabled={!name.trim() || isLoading}
              className="w-full"
            >
              {isLoading ? (
                <Icons.loader className="size-4 animate-spin" />
              ) : (
                'Connect'
              )}
            </Button>
          </YStack>
        )}

        {step.type === 'oauth' && (
          <YStack className="gap-4">
            <YStack className="gap-2">
              <label
                htmlFor="mcp-name-oauth"
                className="text-sm font-medium text-foreground"
              >
                Name
              </label>
              <Input
                id="mcp-name-oauth"
                value={name}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleOAuthConnect();
                }}
                placeholder="Server name"
                className="text-sm"
                autoFocus
              />
            </YStack>
            <p className="text-sm text-foreground-muted">
              This server uses OAuth. You'll be redirected to authorize access.
            </p>
            <Button
              onClick={() => handleOAuthConnect()}
              disabled={!name.trim() || isLoading}
              className="w-full"
            >
              {isLoading ? (
                <Icons.loader className="size-4 animate-spin" />
              ) : (
                'Connect'
              )}
            </Button>
          </YStack>
        )}

        {step.type === 'oauth_polling' && (
          <YStack center className="gap-4 py-4">
            <Icons.loader className="size-6 animate-spin text-foreground-muted" />
            <YStack center className="gap-1">
              <p className="font-medium text-foreground">Connecting...</p>
              <p className="text-center text-sm text-foreground-muted">
                Complete the authorization in your browser. This may take up to
                a minute after authorizing.
              </p>
            </YStack>
            <Button
              variant="secondary"
              className="w-full mt-2"
              onClick={cancelAndClose}
            >
              Cancel
            </Button>
            {fallbackUrl && (
              <p className="text-xs text-foreground-muted">
                Having trouble?{' '}
                <button
                  type="button"
                  className="underline hover:text-foreground"
                  onClick={switchToApiKeyFallback}
                >
                  Connect with an API key instead
                </button>
              </p>
            )}
          </YStack>
        )}

        {step.type === 'oauth_success' && (
          <YStack center className="gap-4 py-4">
            <div className="size-10 rounded-full bg-green-500/10 flex items-center justify-center">
              <Icons.check className="size-5 text-green-500" />
            </div>
            <p className="font-medium text-foreground">
              Connected successfully!
            </p>
            <Button className="w-full" onClick={dismiss}>
              Done
            </Button>
          </YStack>
        )}

        {step.type === 'oauth_cancelled' && (
          <YStack center className="gap-4 py-4">
            <div className="size-10 rounded-full bg-secondary flex items-center justify-center">
              <Icons.cancel className="size-5 text-foreground-muted" />
            </div>
            <YStack center className="gap-1">
              <p className="font-medium text-foreground">
                Authorization cancelled
              </p>
              <p className="text-sm text-foreground-muted">
                The authorization was not completed. You can try again whenever
                you're ready.
              </p>
            </YStack>
            <Button
              className="w-full"
              onClick={() => {
                cleanup();
                const retryUrl = initialUrl || url;
                const retryName = initialName || name;
                if (retryUrl && retryName) {
                  handleAutoConnect(retryUrl, retryName);
                } else {
                  resetState();
                }
              }}
            >
              Try again
            </Button>
            {fallbackUrl && (
              <Button
                variant="secondary"
                className="w-full"
                onClick={switchToApiKeyFallback}
              >
                Use an API key instead
              </Button>
            )}
          </YStack>
        )}

        {step.type === 'oauth_error' && (
          <YStack center className="gap-4 py-4">
            <div className="size-10 rounded-full bg-destructive/10 flex items-center justify-center">
              <Icons.cancel className="size-5 text-destructive" />
            </div>
            <YStack center className="gap-1">
              <p className="font-medium text-foreground">
                {step.reason === 'timeout'
                  ? 'Connection timed out'
                  : 'Connection failed'}
              </p>
              <p className="text-sm text-foreground-muted">
                {step.reason === 'timeout'
                  ? "We didn't hear back in time. The authorization window may have been closed before completing."
                  : (step.detail ?? 'Something went wrong. Please try again.')}
              </p>
            </YStack>
            <Button
              className="w-full"
              onClick={() => {
                cleanup();
                const retryUrl = initialUrl || url;
                const retryName = initialName || name;
                if (retryUrl && retryName) {
                  handleAutoConnect(retryUrl, retryName);
                } else {
                  resetState();
                }
              }}
            >
              Try again
            </Button>
            {fallbackUrl && (
              <Button
                variant="secondary"
                className="w-full"
                onClick={switchToApiKeyFallback}
              >
                Use an API key instead
              </Button>
            )}
          </YStack>
        )}
      </Dialog.Content>
    </Dialog.Root>
  );
};
